import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, ShieldCheck, AlertTriangle,
  Sparkles, ArrowRight, RotateCcw, BookOpen,
} from "lucide-react";

const RATINGS = {
  excellent: { label: "Excellent knowledge",  tone: "emerald", icon: ShieldCheck },
  good:      { label: "Solid awareness",     tone: "sky",     icon: CheckCircle2 },
  risky:     { label: "Risky: keep learning", tone: "amber",  icon: AlertTriangle },
  high_risk: { label: "High risk: review the guides", tone: "rose", icon: XCircle },
};

export default function AcademyQuiz() {
  const { slug } = useParams();
  const [phase, setPhase] = useState("intro"); // intro | playing | finished
  const [quizMeta, setQuizMeta] = useState(null);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [answersLog, setAnswersLog] = useState([]);
  const [finalSummary, setFinalSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setPhase("intro"); setQuizMeta(null); setErr(null);
    api.get(`/academy/quizzes/${slug}`)
      .then(({ data }) => setQuizMeta(data))
      .catch((e) => setErr(formatApiError(e)));
  }, [slug]);

  async function startQuiz() {
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post(`/academy/quizzes/${slug}/start`);
      setSession(data);
      setQuestions(data.questions || []);
      setCurrent(0); setSelected(null); setLastAnswer(null);
      setAnswersLog([]); setFinalSummary(null);
      setPhase("playing");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function submitAnswer() {
    if (selected === null || !questions[current]) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/academy/sessions/${session.id}/answer`, {
        question_id: questions[current].id,
        answer_index: selected,
      });
      setLastAnswer(data);
      setAnswersLog((prev) => [...prev, { ...data, question: questions[current], picked: selected }]);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function nextQuestion() {
    if (current + 1 >= questions.length) {
      await finalizeQuiz();
    } else {
      setCurrent(current + 1);
      setSelected(null); setLastAnswer(null);
    }
  }

  async function finalizeQuiz() {
    setBusy(true);
    try {
      const { data } = await api.post(`/academy/sessions/${session.id}/finalize`);
      setFinalSummary(data);
      setPhase("finished");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  }

  if (err && !quizMeta) {
    return (
      <ShellMessage>
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-4 text-sm">{err}</div>
      </ShellMessage>
    );
  }
  if (!quizMeta) {
    return <ShellMessage><Loading /></ShellMessage>;
  }

  if (phase === "intro") {
    return (
      <Shell quizMeta={quizMeta}>
        <IntroCard quizMeta={quizMeta} onStart={startQuiz} busy={busy} err={err} />
      </Shell>
    );
  }
  if (phase === "playing") {
    return (
      <Shell quizMeta={quizMeta}>
        <PlayingCard
          questions={questions} current={current} selected={selected} setSelected={setSelected}
          lastAnswer={lastAnswer} submitAnswer={submitAnswer} nextQuestion={nextQuestion}
          busy={busy} err={err}
        />
      </Shell>
    );
  }
  // finished
  return (
    <Shell quizMeta={quizMeta}>
      <FinishedCard
        finalSummary={finalSummary} answersLog={answersLog}
        questions={questions} quizMeta={quizMeta} onRetry={startQuiz} busy={busy}
      />
    </Shell>
  );
}

// ---------------------------------------------------------------------------

function ShellMessage({ children }) {
  return <div className="max-w-2xl mx-auto px-4 py-16 text-center">{children}</div>;
}

function Shell({ quizMeta, children }) {
  const backTo = quizMeta?.audience === "business" ? "/businesses/academy" : "/academy";
  return (
    <div data-testid="academy-quiz-page" className="bg-[#FAFAFA] min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to={backTo} data-testid="quiz-back-link"
               className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
          <ArrowLeft className="w-4 h-4" /> Back to Academy
        </Link>
        <div className="mt-4 flex items-center gap-2 text-xs">
          <span className={`px-2 py-0.5 rounded-full font-medium ${audienceClass(quizMeta.audience)}`}>
            {audienceLabel(quizMeta.audience)}
          </span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-500">{quizMeta.active_questions || 0} questions available · {quizMeta.questions_per_session || 10} per round</span>
        </div>
        <h1 data-testid="quiz-title"
             className="font-display text-3xl sm:text-4xl font-semibold mt-2 leading-tight">{quizMeta.title}</h1>
        {quizMeta.description && (
          <p className="text-slate-600 mt-3">{quizMeta.description}</p>
        )}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function IntroCard({ quizMeta, onStart, busy, err }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="quiz-intro-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-xl font-semibold">How it works</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
            <li>You will get {quizMeta.questions_per_session || 10} randomised scenarios from a {quizMeta.active_questions || quizMeta.total_question_target || 50}-question pool.</li>
            <li>Pick the safest action. You'll get immediate feedback and a short explanation for each.</li>
            <li>At the end you receive a percentage score and a Quiz Result. It measures what you know, not the safety or readiness of any vehicle, driver or business.</li>
            <li>No account required, but you can <Link to="/register" className="text-emerald-700 hover:underline">create one</Link> to save your scores.</li>
          </ul>
        </div>
      </div>
      {err && <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}
      <button data-testid="quiz-start-button"
              disabled={busy}
              onClick={onStart}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        Start quiz
      </button>
    </div>
  );
}

function PlayingCard({ questions, current, selected, setSelected, lastAnswer,
                       submitAnswer, nextQuestion, busy, err }) {
  const q = questions[current];
  if (!q) return <Loading />;
  const isAnswered = !!lastAnswer;
  const progressPct = Math.round(((current + (isAnswered ? 1 : 0)) / questions.length) * 100);

  return (
    <div className="space-y-5" data-testid="quiz-playing">
      {/* Progress */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1.5">
          <span data-testid="quiz-progress-text">Question {current + 1} of {questions.length}</span>
          <span>{progressPct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Scenario */}
      {q.scenario_text && (
        <div className="rounded-2xl bg-slate-900 text-slate-100 p-5" data-testid="quiz-scenario">
          <div className="text-xs uppercase tracking-[0.18em] text-emerald-300 font-semibold">Scenario</div>
          <p className="mt-2 leading-relaxed text-slate-100">{q.scenario_text}</p>
        </div>
      )}

      {/* Question */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="font-display text-lg font-semibold" data-testid="quiz-question-text">{q.question_text}</div>
        <div className="mt-4 space-y-2">
          {q.answer_options.map((opt, idx) => {
            const isPicked = selected === idx;
            const isCorrect = isAnswered && idx === lastAnswer.correct_answer_index;
            const isWrongPick = isAnswered && idx === lastAnswer.answer_index && !lastAnswer.correct;
            let cls = "border-slate-200 hover:border-emerald-300";
            if (isAnswered) {
              if (isCorrect) cls = "border-emerald-400 bg-emerald-50";
              else if (isWrongPick) cls = "border-rose-400 bg-rose-50";
              else cls = "border-slate-200 opacity-70";
            } else if (isPicked) {
              cls = "border-emerald-500 bg-emerald-50";
            }
            return (
              <button key={idx}
                      data-testid={`quiz-option-${idx}`}
                      disabled={isAnswered || busy}
                      onClick={() => setSelected(idx)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition flex items-start gap-3 ${cls}`}>
                <span className="mt-0.5 w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="text-sm text-slate-800 leading-relaxed">{opt}</span>
                {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto flex-shrink-0" />}
                {isWrongPick && <XCircle className="w-5 h-5 text-rose-600 ml-auto flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {isAnswered && (
          <div className={`mt-4 rounded-xl p-4 ${lastAnswer.correct ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}
                data-testid="quiz-explanation">
            <div className={`flex items-center gap-2 text-sm font-semibold ${lastAnswer.correct ? "text-emerald-800" : "text-rose-800"}`}>
              {lastAnswer.correct ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {lastAnswer.correct ? "Correct: that's the safest call." : "Not quite: here's the safer move."}
            </div>
            <div className="text-sm text-slate-700 mt-2 leading-relaxed">{lastAnswer.explanation}</div>
          </div>
        )}

        {err && <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          {!isAnswered ? (
            <button data-testid="quiz-submit-answer"
                    disabled={selected === null || busy}
                    onClick={submitAnswer}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit answer
            </button>
          ) : (
            <button data-testid="quiz-next-question"
                    disabled={busy}
                    onClick={nextQuestion}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {current + 1 >= questions.length ? "See my quiz result" : "Next question"}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FinishedCard({ finalSummary, answersLog, questions, quizMeta, onRetry, busy }) {
  if (!finalSummary) return <Loading />;
  const r = RATINGS[finalSummary.rating] || RATINGS.risky;
  const RIcon = r.icon;
  const isBiz = quizMeta?.audience === "business";
  const backTo = isBiz ? "/businesses/academy" : "/academy";
  const signupTo = isBiz ? "/register?as=business" : "/register";
  const toneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky:     "bg-sky-50 border-sky-200 text-sky-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  }[r.tone];

  return (
    <div className="space-y-5" data-testid="quiz-finished">
      {/* Score card */}
      <div className={`rounded-2xl border-2 p-6 sm:p-8 ${toneCls}`} data-testid="quiz-final-card">
        <div className="flex items-center gap-3">
          <RIcon className="w-7 h-7" />
          <div className="text-xs uppercase tracking-[0.18em] font-semibold">Your quiz result</div>
        </div>
        <div className="mt-2 font-display text-3xl sm:text-4xl font-semibold">{r.label}</div>
        <div className="mt-1 text-sm">
          You scored <span data-testid="quiz-final-score" className="font-semibold">{finalSummary.score}/{questions.length}</span>
          <span className="ml-2">({finalSummary.percentage}%)</span>
        </div>
      </div>

      {/* Save score CTA (anonymous-first) */}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5"
            data-testid="quiz-save-score-cta">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Save your quiz score
            </div>
            <div className="font-display text-lg font-semibold mt-0.5">Create a free account to track your progress</div>
            <div className="text-sm text-slate-700 mt-1">Save your quiz scores and learning badges, and pass quizzes to unlock reviewer privileges later.</div>
          </div>
          <Link to={signupTo} data-testid="quiz-cta-signup"
                 className="inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] whitespace-nowrap">
            {isBiz ? "Create business account" : "Create free account"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Review answers */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="quiz-review-section">
        <div className="font-display text-lg font-semibold">Review your answers</div>
        <div className="mt-3 space-y-3">
          {answersLog.map((a, idx) => (
            <div key={idx} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start gap-2">
                {a.correct
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  : <XCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />}
                <div className="text-sm text-slate-800 font-medium flex-1">{a.question.question_text}</div>
              </div>
              <div className="text-xs text-slate-600 mt-2 leading-relaxed pl-6">{a.explanation}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="quiz-retry-button"
                disabled={busy}
                onClick={onRetry}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium text-sm hover:bg-slate-50 disabled:opacity-60">
          <RotateCcw className="w-4 h-4" /> Try again with new questions
        </button>
        <Link to={backTo} data-testid="quiz-back-to-academy"
               className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium text-sm hover:bg-slate-50">
          <BookOpen className="w-4 h-4" /> Back to Academy
        </Link>
      </div>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
}

function audienceClass(a) {
  if (a === "renter")   return "bg-sky-50 text-sky-700";
  if (a === "business") return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-700";
}
function audienceLabel(a) {
  if (a === "renter")   return "For renters";
  if (a === "business") return "For operators";
  return "Everyone";
}
